import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { apiService } from '../../../../services/api';
import { OrcamentoDetalhado, OrcamentoProdutoDetalhado } from '../../../../types/orcamentos_multiplos';
import { toast } from 'react-toastify';
import { formatCurrencyBRL } from '../../../../utils/formatters';

interface OrcamentosVisualizacaoProps {
    oportunidadeId: number;
}

type SortEdital = 'asc' | 'desc' | null;

const OrcamentosVisualizacao: React.FC<OrcamentosVisualizacaoProps> = ({ oportunidadeId }) => {
    const [orcamentos, setOrcamentos] = useState<OrcamentoDetalhado[]>([]);
    const [produtosPorOrcamento, setProdutosPorOrcamento] = useState<Record<number, OrcamentoProdutoDetalhado[]>>({});
    const [minValueByProduct, setMinValueByProduct] = useState<Map<number, number>>(new Map());
    const [loading, setLoading] = useState(true);
    const [loadingProdutos, setLoadingProdutos] = useState(false);
    const [orcamentoSelecionado, setOrcamentoSelecionado] = useState<OrcamentoDetalhado | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [sortEdital, setSortEdital] = useState<SortEdital>(null);

    const produtosCarregadosRef = useRef(false);

    const formatCurrency = (value: number | string): string => formatCurrencyBRL(value);

    const formatDate = (dateString: string | null): string => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    useEffect(() => {
        if (oportunidadeId) {
            carregarOrcamentos();
        }
    }, [oportunidadeId]);

    const carregarOrcamentos = async () => {
        try {
            setLoading(true);
            const response = await apiService.getOrcamentos({ id_oportunidade: oportunidadeId });
            if (response.success && Array.isArray(response.data)) {
                const ordenados = [...response.data].sort((a, b) => b.versao - a.versao);
                setOrcamentos(ordenados);
                if (ordenados.length > 0) {
                    carregarTodosProdutos(ordenados);
                }
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao carregar orçamentos');
        } finally {
            setLoading(false);
        }
    };

    const carregarTodosProdutos = async (lista: OrcamentoDetalhado[]) => {
        if (produtosCarregadosRef.current) return;
        produtosCarregadosRef.current = true;
        setLoadingProdutos(true);
        try {
            const resultados = await Promise.all(
                lista.map(orc =>
                    apiService.getOrcamentoProdutos({ id_orcamento: orc.id })
                        .then(r => ({ id: orc.id, produtos: r.success && Array.isArray(r.data) ? r.data as OrcamentoProdutoDetalhado[] : [] }))
                        .catch(() => ({ id: orc.id, produtos: [] as OrcamentoProdutoDetalhado[] }))
                )
            );

            const mapa: Record<number, OrcamentoProdutoDetalhado[]> = {};
            const minMap = new Map<number, number>();

            for (const { id, produtos } of resultados) {
                mapa[id] = produtos;
                for (const p of produtos) {
                    const val = parseFloat(p.valor_unitario);
                    const idProd = p.id_produto_oportunidade;
                    if (!minMap.has(idProd) || val < minMap.get(idProd)!) {
                        minMap.set(idProd, val);
                    }
                }
            }

            setProdutosPorOrcamento(mapa);
            setMinValueByProduct(minMap);
        } finally {
            setLoadingProdutos(false);
        }
    };

    const abrirModal = (orcamento: OrcamentoDetalhado) => {
        setSortEdital(null);
        setOrcamentoSelecionado(orcamento);
        setShowModal(true);
    };

    const fecharModal = () => {
        setShowModal(false);
        setOrcamentoSelecionado(null);
    };

    const isMinValue = (idProdOportunidade: number, valorUnitario: string): boolean => {
        if (orcamentos.length <= 1) return false;
        const min = minValueByProduct.get(idProdOportunidade);
        if (min === undefined) return false;
        return parseFloat(valorUnitario) === min;
    };

    const calcularTotalGeral = (produtos: OrcamentoProdutoDetalhado[]): number =>
        produtos.reduce((acc, p) => acc + parseFloat(p.valor_total_item || '0'), 0);

    const toggleSortEdital = () => {
        setSortEdital(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc');
    };

    const getSortIcon = () => {
        if (sortEdital === 'asc') return <i className="ti ti-sort-ascending ms-1" />;
        if (sortEdital === 'desc') return <i className="ti ti-sort-descending ms-1" />;
        return <i className="ti ti-arrows-sort ms-1 text-muted opacity-50" />;
    };

    const produtosBase = orcamentoSelecionado ? (produtosPorOrcamento[orcamentoSelecionado.id] || []) : [];
    const produtosSelecionado = sortEdital === null
        ? produtosBase
        : [...produtosBase].sort((a, b) => {
            const edA = a.numero_edital || '';
            const edB = b.numero_edital || '';
            return sortEdital === 'asc'
                ? edA.localeCompare(edB, 'pt-BR', { numeric: true })
                : edB.localeCompare(edA, 'pt-BR', { numeric: true });
        });

    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="text-muted mt-2 mb-0">Carregando orçamentos...</p>
                </div>
            </div>
        );
    }

    if (orcamentos.length === 0) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="avatar avatar-xl bg-light-primary rounded-circle mb-3 mx-auto">
                        <i className="ti ti-files fs-36 text-primary" />
                    </div>
                    <h5 className="mb-1">Nenhum orçamento cadastrado</h5>
                    <p className="text-muted mb-0">Esta oportunidade ainda não possui orçamentos registrados.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="fw-semibold mb-1">Orçamentos</h4>
                        <p className="text-muted mb-0">
                            {orcamentos.length} orçamento{orcamentos.length !== 1 ? 's' : ''} encontrado{orcamentos.length !== 1 ? 's' : ''}
                            {orcamentos.length > 1 && (
                                <span className="ms-2 text-success small">
                                    <i className="ti ti-arrow-badge-down me-1" />
                                    Menor preço indicado na tabela de produtos
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        {orcamentos.map((orc) => (
                            <div key={orc.id} className="col-md-6 col-lg-4">
                                <div className="card border h-100 shadow-sm">
                                    <div className="card-header bg-light py-2">
                                        <span className="fw-semibold text-dark">{orc.titulo}</span>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-2">
                                            <small className="text-muted d-block">Fornecedor</small>
                                            <span className="fw-medium">{orc.fornecedor_nome || 'Não informado'}</span>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-muted d-block">Valor Total</small>
                                            <span className="fw-bold text-primary fs-5">{formatCurrency(orc.valor_total)}</span>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block">Produtos</small>
                                            <span className="fw-medium">{orc.total_produtos}</span>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-white d-flex justify-content-between align-items-center py-2">
                                        <small className="text-muted">{formatDate(orc.data_criacao)}</small>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => abrirModal(orc)}
                                        >
                                            <i className="ti ti-eye me-1" />
                                            Visualizar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Modal de Detalhe */}
            <Modal show={showModal} onHide={fecharModal} size="xl" centered scrollable>
                <Modal.Header closeButton className="border-bottom py-3">
                    <div>
                        <Modal.Title className="fw-bold fs-5">
                            {orcamentoSelecionado?.titulo}
                        </Modal.Title>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {orcamentoSelecionado && (
                        <>
                            {/* Bloco único com separador interno */}
                            <div className="border rounded mb-4 overflow-hidden">
                                <div className="row g-0">
                                    {/* Fornecedor */}
                                    <div className="col-md-5 p-4 border-end">
                                        <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                                            <i className="ti ti-building-store text-primary fs-5" />
                                            <span className="fw-semibold">Fornecedor</span>
                                        </div>
                                        <p className="fw-bold mb-1">
                                            {orcamentoSelecionado.fornecedor_nome || 'Não informado'}
                                        </p>
                                        {orcamentoSelecionado.fornecedor_tipo && (
                                            <p className="text-muted mb-2 small">{orcamentoSelecionado.fornecedor_tipo}</p>
                                        )}
                                        {orcamentoSelecionado.fornecedor_email && (
                                            <p className="mb-1 small text-muted">
                                                <i className="ti ti-mail me-2" />
                                                {orcamentoSelecionado.fornecedor_email}
                                            </p>
                                        )}
                                        {orcamentoSelecionado.fornecedor_telefone && (
                                            <p className="mb-0 small text-muted">
                                                <i className="ti ti-phone me-2" />
                                                {orcamentoSelecionado.fornecedor_telefone}
                                            </p>
                                        )}
                                    </div>

                                    {/* Detalhes */}
                                    <div className="col-md-7 p-4">
                                        <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                                            <i className="ti ti-info-circle text-primary fs-5" />
                                            <span className="fw-semibold">Detalhes</span>
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-sm-6">
                                                <div className="text-muted small mb-1">Criado em</div>
                                                <div className="fw-medium small">{formatDate(orcamentoSelecionado.data_criacao)}</div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="text-muted small mb-1">Criado por</div>
                                                <div className="fw-medium small">{orcamentoSelecionado.criado_por_nome || '-'}</div>
                                            </div>
                                            {orcamentoSelecionado.descricao && (
                                                <div className="col-12">
                                                    <div className="text-muted small mb-1">Descrição</div>
                                                    <div className="small">{orcamentoSelecionado.descricao}</div>
                                                </div>
                                            )}
                                            {orcamentoSelecionado.observacoes && (
                                                <div className="col-12">
                                                    <div className="text-muted small mb-1">Observações</div>
                                                    <div className="small">{orcamentoSelecionado.observacoes}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Tabela de Produtos */}
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <i className="ti ti-package text-primary fs-5" />
                                <span className="fw-semibold">Produtos do Orçamento</span>
                                {orcamentos.length > 1 && (
                                    <span className="ms-auto text-muted small">
                                        <i className="ti ti-arrow-badge-down text-success me-1" />
                                        Menor preço entre os orçamentos
                                    </span>
                                )}
                            </div>

                            {loadingProdutos ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                    <p className="text-muted mt-2 mb-0 small">Carregando produtos...</p>
                                </div>
                            ) : produtosSelecionado.length === 0 ? (
                                <div className="alert alert-light border text-center">
                                    <i className="ti ti-package-off me-1" />
                                    Nenhum produto neste orçamento.
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover align-middle border">
                                        <thead>
                                            <tr className="table-light">
                                                <th style={{ width: '36px' }} className="text-center text-muted">#</th>
                                                <th>Produto</th>
                                                <th
                                                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                    onClick={toggleSortEdital}
                                                    title="Ordenar por Nº Edital"
                                                >
                                                    Nº Edital {getSortIcon()}
                                                </th>
                                                <th>Marca</th>
                                                <th>Descrição</th>
                                                <th className="text-end">Qtd</th>
                                                <th>Und.</th>
                                                <th className="text-end">Valor Unit.</th>
                                                <th className="text-end">Valor Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {produtosSelecionado.map((p, idx) => {
                                                const melhorPreco = isMinValue(p.id_produto_oportunidade, p.valor_unitario);
                                                return (
                                                    <tr key={p.id}>
                                                        <td className="text-center text-muted">{idx + 1}</td>
                                                        <td className="fw-medium">{p.produto_nome}</td>
                                                        <td className="small">{p.numero_edital || '-'}</td>
                                                        <td className="small">{p.marca || '-'}</td>
                                                        <td
                                                            className="small text-muted"
                                                            style={{
                                                                maxWidth: '180px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                            title={p.descricao || undefined}
                                                        >
                                                            {p.descricao || '-'}
                                                        </td>
                                                        <td className="text-end small">
                                                            {parseFloat(p.quantidade).toLocaleString('pt-BR')}
                                                        </td>
                                                        <td className="small text-muted">{p.unidade_medida || '-'}</td>
                                                        <td
                                                            className="text-end small"
                                                            style={melhorPreco ? {
                                                                backgroundColor: '#d1f0d1',
                                                                color: '#166534',
                                                                fontWeight: 700,
                                                            } : undefined}
                                                            title={melhorPreco ? 'Menor valor entre todos os orçamentos' : undefined}
                                                        >
                                                            {melhorPreco && <i className="ti ti-arrow-badge-down me-1" />}
                                                            {formatCurrency(p.valor_unitario)}
                                                        </td>
                                                        <td className="text-end small">{formatCurrency(p.valor_total_item)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr className="table-light">
                                                <td colSpan={8} className="text-end fw-semibold">Total Geral</td>
                                                <td className="text-end fw-bold text-primary">
                                                    {formatCurrency(calcularTotalGeral(produtosSelecionado))}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-top">
                    <button type="button" className="btn btn-light" onClick={fecharModal}>
                        Fechar
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default OrcamentosVisualizacao;
